import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { bestProducts } from '@/mocks/products';

interface QuickBuySectionProps {
  pointBalance: number;
  userId: string;
}

interface QuickBuyProduct {
  id: number;
  name: string;
  price: number;
  usdtPrice?: number;
  originalPrice: number;
  discount: number;
  image: string;
  badge: string;
  description: string;
  stock: number;
  category: string;
  weight: string;
  rating: number;
  reviews: number;
}

type SortMode = 'affordable' | 'popular' | 'discount';

export default function QuickBuySection({ pointBalance, userId }: QuickBuySectionProps) {
  const navigate = useNavigate();
  const [sortMode, setSortMode] = useState<SortMode>('affordable');
  const [addedId, setAddedId] = useState<number | null>(null);

  const formatPoint = (n: number) =>
    parseFloat(n.toFixed(2)).toLocaleString('ko-KR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatPrice = (n: number) => n.toLocaleString('ko-KR');

  const products: QuickBuyProduct[] = bestProducts;

  const sortedProducts = [...products].sort((a, b) => {
    if (sortMode === 'affordable') {
      // 살 수 있는 것 먼저, 그 다음 가격 낮은 순
      const aCanBuy = pointBalance >= a.price ? 1 : 0;
      const bCanBuy = pointBalance >= b.price ? 1 : 0;
      if (aCanBuy !== bCanBuy) return bCanBuy - aCanBuy;
      return a.price - b.price;
    }
    if (sortMode === 'popular') return b.reviews - a.reviews;
    if (sortMode === 'discount') return b.discount - a.discount;
    return 0;
  });

  const affordableCount = products.filter((p) => pointBalance >= p.price).length;

  const handleQuickBuy = (product: QuickBuyProduct) => {
    // 장바구니에 추가 후 주문 페이지로 이동
    setAddedId(product.id);
    setTimeout(() => {
      setAddedId(null);
      navigate(`/order?product_id=${product.id}&from_points=1&user_id=${encodeURIComponent(userId)}`);
    }, 600);
  };

  const handleViewAll = () => {
    navigate('/products');
  };

  const sortOptions: { id: SortMode; label: string; icon: string }[] = [
    { id: 'affordable', label: '구매 가능', icon: 'ri-coin-line' },
    { id: 'popular', label: '인기순', icon: 'ri-fire-line' },
    { id: 'discount', label: '할인율순', icon: 'ri-percent-line' },
  ];

  return (
    <div className="bg-white rounded-2xl overflow-hidden">
      {/* 헤더 */}
      <div className="px-6 md:px-8 pt-6 md:pt-8 pb-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-1">
          <div>
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2" style={{ fontFamily: 'Noto Serif KR, serif' }}>
              <i className="ri-flashlight-line w-5 h-5 flex items-center justify-center text-emerald-600"></i>
              포인트로 바로 구매
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              현재 <span className="font-bold text-emerald-600">{formatPoint(pointBalance)}P</span> 보유 중 ·{' '}
              {affordableCount > 0 ? (
                <span className="text-emerald-600 font-bold">{affordableCount}개 제품</span>
              ) : (
                <span className="text-gray-400">구매 가능한 제품 없음</span>
              )}
              {affordableCount > 0 && ' 즉시 구매 가능'}
            </p>
          </div>
          <button
            type="button"
            onClick={handleViewAll}
            className="flex-shrink-0 text-xs font-bold text-emerald-600 hover:text-emerald-700 flex items-center gap-1 cursor-pointer whitespace-nowrap"
          >
            전체 제품 보기
            <i className="ri-arrow-right-line w-3.5 h-3.5 flex items-center justify-center"></i>
          </button>
        </div>

        {/* 정렬 탭 */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1 w-fit mt-4">
          {sortOptions.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setSortMode(opt.id)}
              className={`px-3 py-1 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap flex items-center gap-1 ${
                sortMode === opt.id
                  ? 'bg-white text-emerald-700'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className={`${opt.icon} w-3 h-3 flex items-center justify-center`}></i>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* 제품 목록 */}
      <div className="px-6 md:px-8 pb-6 md:pb-8">
        <div className="space-y-3">
          {sortedProducts.map((product) => {
            const canBuy = pointBalance >= product.price;
            const shortage = product.price - pointBalance;
            const isAdding = addedId === product.id;

            return (
              <div
                key={product.id}
                className={`relative flex items-center gap-4 p-4 rounded-2xl border-2 transition-all duration-200 ${
                  canBuy
                    ? 'border-emerald-100 bg-emerald-50/40 hover:border-emerald-300 hover:bg-emerald-50'
                    : 'border-gray-100 bg-gray-50/50 opacity-70'
                }`}
              >
                {/* 제품 이미지 */}
                <div className="relative w-20 h-20 flex-shrink-0 rounded-xl overflow-hidden bg-white">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-top"
                  />
                  {product.badge && (
                    <span className="absolute top-1 left-1 px-1.5 py-0.5 bg-emerald-500 text-white text-[10px] font-bold rounded-md leading-none">
                      {product.badge}
                    </span>
                  )}
                  {!canBuy && (
                    <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
                      <i className="ri-lock-line text-gray-400 text-lg"></i>
                    </div>
                  )}
                </div>

                {/* 제품 정보 */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-900 truncate">{product.name}</p>
                  <p className="text-xs text-gray-400 mt-0.5 truncate">{product.weight}</p>

                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span className={`text-base font-black ${canBuy ? 'text-emerald-600' : 'text-gray-500'}`}>
                      {formatPrice(product.price)}P
                    </span>
                    {product.originalPrice > product.price && (
                      <span className="text-xs text-gray-400 line-through">{formatPrice(product.originalPrice)}원</span>
                    )}
                    {product.discount > 0 && (
                      <span className="text-xs font-bold text-red-500">{product.discount}%↓</span>
                    )}
                  </div>

                  {/* 포인트 부족 안내 */}
                  {!canBuy && (
                    <div className="flex items-center gap-1 mt-1.5">
                      <i className="ri-coins-line w-3 h-3 flex items-center justify-center text-amber-500 text-xs"></i>
                      <span className="text-xs text-amber-600 font-semibold">
                        {formatPoint(shortage)}P 부족
                      </span>
                    </div>
                  )}

                  {/* 구매 가능 뱃지 */}
                  {canBuy && (
                    <div className="flex items-center gap-1 mt-1.5">
                      <i className="ri-checkbox-circle-fill w-3 h-3 flex items-center justify-center text-emerald-500 text-xs"></i>
                      <span className="text-xs text-emerald-600 font-semibold">구매 가능</span>
                    </div>
                  )}
                </div>

                {/* 구매 버튼 */}
                <div className="flex-shrink-0">
                  {canBuy ? (
                    <button
                      type="button"
                      onClick={() => handleQuickBuy(product)}
                      disabled={isAdding}
                      className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                        isAdding
                          ? 'bg-emerald-500 text-white scale-95'
                          : 'bg-emerald-600 text-white hover:bg-emerald-700 active:scale-95'
                      }`}
                    >
                      {isAdding ? (
                        <><i className="ri-check-line w-3.5 h-3.5 flex items-center justify-center"></i>이동 중</>
                      ) : (
                        <><i className="ri-flashlight-line w-3.5 h-3.5 flex items-center justify-center"></i>바로 구매</>
                      )}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => navigate('/point-purchase')}
                      className="px-4 py-2.5 rounded-xl text-xs font-bold bg-amber-100 text-amber-700 hover:bg-amber-200 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                    >
                      <i className="ri-add-circle-line w-3.5 h-3.5 flex items-center justify-center"></i>
                      충전하기
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* 하단 전체 보기 */}
        <button
          type="button"
          onClick={handleViewAll}
          className="w-full mt-4 py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm font-bold text-gray-400 hover:border-emerald-300 hover:text-emerald-600 hover:bg-emerald-50/50 transition-all cursor-pointer flex items-center justify-center gap-2"
        >
          <i className="ri-grid-line w-4 h-4 flex items-center justify-center"></i>
          더 많은 제품 보기
        </button>
      </div>
    </div>
  );
}
